/**
 * What the delete dialog says about a team before the confirm button,
 * from GET .../delete-preview (api/app/services/team_delete.py). Pure, so
 * the owner's dialog and the staff one read the same lines, and the lines
 * can be tested without a render.
 */

import type { TeamDeleteConversations, TeamDeletePreview } from '@/api/generated/model'

/** Said plainly in every dialog: the gateway removes both with the team. */
export const CONVERSATIONS_GO =
  'Conversations and usage records are deleted too. The API keys are erased.'

export interface PreviewLine {
  label: string
  detail: string
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`
}

/** `12 conversations and 340 calls`; a capped count reads `100000 or more calls`. */
export function conversationsLine(count: TeamDeleteConversations): string {
  const floor = (n: number, one: string, many: string): string =>
    count.capped ? `${String(n)} or more ${many}` : plural(n, one, many)
  return `${floor(count.conversations, 'conversation', 'conversations')} and ${floor(count.calls, 'call', 'calls')}`
}

/** The short list the dialog shows: what goes, in the order it goes. */
export function previewLines(preview: TeamDeletePreview): PreviewLine[] {
  const lines: PreviewLine[] = []
  if (preview.conversations !== null) {
    lines.push({ label: 'Conversations', detail: conversationsLine(preview.conversations) })
  }
  if (preview.jobs.length > 0) {
    lines.push({ label: 'Jobs', detail: preview.jobs.map((job) => job.name).join(', ') })
  }
  if (preview.gateway_team_id !== null) {
    lines.push({
      label: 'Gateway team',
      detail: `${preview.gateway_team_id} and every API key it has`,
    })
  }
  if (preview.folders.length > 0) {
    const objects = preview.folders.reduce((sum, folder) => sum + folder.objects, 0)
    lines.push({
      label: 'Bucket folders',
      detail: `${plural(objects, 'file', 'files')} in ${preview.folders
        .map((folder) => folder.prefix)
        .join(', ')}`,
    })
  }
  if (preview.tables.length > 0) {
    const rows = preview.tables.reduce((sum, table) => sum + table.rows, 0)
    lines.push({
      label: 'Database rows',
      detail: `${plural(rows, 'row', 'rows')} in ${preview.tables
        .map((table) => `${table.table} (${String(table.rows)})`)
        .join(', ')}`,
    })
  }
  lines.push({ label: 'Login group', detail: 'The team and its members list' })
  return lines
}

/**
 * Whether the confirm button may be pressed: the preview has loaded, it
 * names no blocker, and the typed name is the team's.
 */
export function canConfirmDelete(
  preview: TeamDeletePreview | undefined,
  typed: string,
  name: string,
): boolean {
  return preview !== undefined && preview.blockers.length === 0 && typed === name
}
